import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertCircle, ArrowLeft, Eye, EyeOff, LockKeyhole, MailCheck, ShieldCheck, Sparkles } from "lucide-react";
import BrandMark from "@/components/BrandMark";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");
    try {
      const user = await login(form.identifier.trim(), form.password);
      toast.success(`Selamat datang, ${user.name}`);
      navigate(user.role === "platform_owner" ? "/platform" : "/app");
    } catch (error) {
      const message = error?.response?.data?.detail || "Login gagal. Periksa kembali email/nomor HP dan password Anda.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-slate-100">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,.18),transparent_30%),radial-gradient(circle_at_80%_80%,rgba(99,102,241,.22),transparent_34%)]" />
      <div className="relative mx-auto grid min-h-screen max-w-7xl lg:grid-cols-[1.05fr_.95fr]">
        <section className="hidden flex-col justify-between p-12 lg:flex xl:p-16">
          <Link to="/" className="w-fit" data-testid="login-brand"><BrandMark size="lg" className="text-white" accentClassName="text-cyan-400" /></Link>
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200"><Sparkles className="h-3.5 w-3.5" /> Workspace operasional</div>
            <h1 className="mt-7 font-display text-6xl font-black leading-[0.95] tracking-[-0.06em] text-white">Satu akun untuk seluruh aktivitas outlet.</h1>
            <p className="mt-6 max-w-lg text-lg leading-8 text-slate-300">Akses kasir, penjualan, stok real-time, QRIS, laporan, dan pengaturan tim melalui workspace KRAYA.</p>
            <div className="mt-9 grid grid-cols-2 gap-3">
              {["Akses berbasis role", "Audit aktivitas", "Data multi-outlet", "Rekonsiliasi QRIS"].map((item) => <div key={item} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.05] p-4 text-sm text-slate-200"><ShieldCheck className="h-5 w-5 text-cyan-300" />{item}</div>)}
            </div>
          </div>
          <div className="text-xs text-slate-500">KRAYA · Powered By AntSoft ID</div>
        </section>

        <section className="flex items-center justify-center p-5 sm:p-8 lg:p-12">
          <div className="w-full max-w-md rounded-[2rem] border border-white/10 bg-white p-6 text-slate-950 shadow-2xl shadow-black/30 sm:p-9">
            <div className="flex items-center justify-between lg:hidden">
              <Link to="/"><BrandMark accentClassName="text-blue-600" /></Link>
              <Link to="/" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><ArrowLeft className="h-3.5 w-3.5" /> Beranda</Link>
            </div>
            <Link to="/" className="hidden w-fit items-center gap-1 text-xs font-semibold text-slate-500 transition hover:text-slate-950 lg:inline-flex"><ArrowLeft className="h-3.5 w-3.5" /> Kembali ke beranda</Link>
            <div className="mt-8 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><LockKeyhole className="h-6 w-6" /></div>
            <h2 className="mt-5 font-display text-4xl font-black tracking-[-0.05em]">Masuk ke KRAYA.</h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">Gunakan email atau nomor handphone yang terdaftar.</p>

            {errorMessage && (
              <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-800" role="alert" data-testid="login-error-message">
                <div className="flex gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
                {errorMessage.toLowerCase().includes("verifikasi") && form.identifier.includes("@") && (
                  <Link
                    to={`/verify-email?email=${encodeURIComponent(form.identifier.trim())}`}
                    className="mt-3 inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-bold text-rose-700 shadow-sm ring-1 ring-rose-200"
                  >
                    <MailCheck className="h-4 w-4" /> Kirim ulang email verifikasi
                  </Link>
                )}
              </div>
            )}

            <form onSubmit={submit} className="mt-7 space-y-5">
              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Email atau nomor HP</label>
                <input data-testid="login-identifier-input" value={form.identifier} onChange={(event) => setForm({ ...form, identifier: event.target.value })} required autoComplete="username" className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="nama@bisnis.com atau 0812xxxx" />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label className="block text-sm font-semibold text-slate-700">Password</label>
                  <Link to="/forgot-password" className="text-xs font-bold text-blue-600 hover:text-blue-700">Lupa password?</Link>
                </div>
                <div className="relative">
                  <input data-testid="login-password-input" type={showPassword ? "text" : "password"} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required autoComplete="current-password" className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 pr-12 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Masukkan password" />
                  <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-400 transition hover:text-slate-700" aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}>{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                </div>
              </div>
              <button type="submit" data-testid="login-submit-btn" disabled={loading} className="flex w-full items-center justify-center rounded-2xl bg-slate-950 py-3.5 text-sm font-bold text-white shadow-lg shadow-slate-900/10 transition hover:-translate-y-0.5 hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50">
                {loading ? "Memverifikasi akun…" : "Masuk"}
              </button>
            </form>
            <div className="mt-7 border-t border-slate-200 pt-6 text-center text-sm text-slate-500">Belum punya akun? <Link to="/register" className="font-bold text-blue-600 hover:text-blue-700" data-testid="login-to-register">Daftar mitra</Link></div>
            <div className="mt-6 text-center text-[11px] uppercase tracking-[0.16em] text-slate-400">Powered By AntSoft ID</div>
          </div>
        </section>
      </div>
    </div>
  );
}
